// Web Push helper (§225) — free browser/PWA notifications for staff. Wraps the browser
// Push API + our backend (pushApi). The service worker's push handler lives in
// public/push-sw.js (imported into the workbox-generated SW via vite.config importScripts).
import { pushApi } from './api'

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export type PushPermission = NotificationPermission | 'unsupported'

export function pushPermission(): PushPermission {
  if (!pushSupported()) return 'unsupported'
  return Notification.permission
}

// VAPID public keys are base64url; the browser's applicationServerKey wants a Uint8Array.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

// isPushEnabled reports whether this browser already has an active push subscription.
export async function isPushEnabled(): Promise<boolean> {
  if (!pushSupported()) return false
  try {
    const reg = await navigator.serviceWorker.ready
    return !!(await reg.pushManager.getSubscription())
  } catch {
    return false
  }
}

// enablePush requests permission, subscribes via the SW, and registers the subscription with
// the backend. reason distinguishes the failure so the UI can guide the user.
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' }
  const perm = await Notification.requestPermission()
  if (perm !== 'granted') return { ok: false, reason: perm } // 'denied' | 'default'
  const { data } = await pushApi.vapidKey()
  if (!data.configured || !data.public_key) return { ok: false, reason: 'not-configured' }
  const reg = await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key) as BufferSource,
    })
  }
  await pushApi.subscribe(sub.toJSON())
  return { ok: true }
}

// disablePush unsubscribes locally and tells the backend to forget the endpoint.
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      await pushApi.unsubscribe(sub.endpoint).catch(() => {})
      await sub.unsubscribe().catch(() => {})
    }
  } catch {
    /* best-effort */
  }
}
